/**
 * TeamStatsBarChart - Animated bar chart showing team game history for game props
 * Features: animated bars, stats summary row, animated tabs, tap-to-expand game detail,
 * gradient fills, home/away indicators, responsive layout
 * Supports: spread, total, and moneyline prop types
 */

import { plotFraction } from '@parlay-picker/shared/chartScale';
import { hitSummary, teamLineOutcome } from '@parlay-picker/shared/lineOutcome';
import {
  computeNiceTicks,
  formatDate,
  formatDateCompact,
  formatDateLong,
  marketKeyToChartPeriod,
} from '@parlay-picker/shared/statsChart';
import { cn } from '@utils/cn';
import { AnimatePresence, motion } from 'framer-motion';
import { Info } from 'lucide-react';
import { type ReactElement, useCallback, useMemo, useState } from 'react';
import {
  formatLineDisplay,
  getDirectionLabel,
  getValueForPeriod,
} from './team-stats-chart/helpers';
import { TeamAnimatedBar } from './team-stats-chart/TeamAnimatedBar';
import { TeamGameDetailCard } from './team-stats-chart/TeamGameDetailCard';
import { TeamStatsSummaryRow } from './team-stats-chart/TeamStatsSummaryRow';
import { TeamViewModeTabs } from './team-stats-chart/TeamViewModeTabs';
import type { ChartDataPoint, TeamStatsBarChartProps, ViewMode } from './team-stats-chart/types';

// TeamGameData re-exported from shared for existing consumers
export type { TeamGameData } from '@parlay-picker/shared/statsChart';

export default function TeamStatsBarChart({
  recentGames,
  h2hGames,
  propLine,
  propType,
  teamName,
  opponentAbbr,
  overUnder,
  isCustomLine = false,
  marketKey,
}: TeamStatsBarChartProps): ReactElement | null {
  const [viewMode, setViewMode] = useState<ViewMode>('L5');
  const [expandedBar, setExpandedBar] = useState<number | null>(null);

  const isUnderBet = overUnder?.toLowerCase() === 'under';
  const period = useMemo(() => marketKeyToChartPeriod(marketKey), [marketKey]);

  const displayGames = useMemo(() => {
    if (!recentGames?.length) return [];
    if (viewMode === 'H2H') return h2hGames?.slice(0, 5) || [];
    const count = viewMode === 'L5' ? 5 : viewMode === 'L10' ? 10 : 20;
    return recentGames.slice(0, count);
  }, [viewMode, recentGames, h2hGames]);

  const chartData: ChartDataPoint[] = useMemo(
    () =>
      [...displayGames].reverse().map((g, idx) => {
        const { value, hasPeriodData } = getValueForPeriod(g, propType, period);
        return {
          value,
          date: g.date,
          gameNumber: idx + 1,
          opponent: g.opponent,
          isHome: g.isHome,
          teamScore: g.teamScore,
          opponentScore: g.opponentScore,
          won: g.won,
          label: formatDate(g.date) || `G${idx + 1}`,
          compactLabel: formatDateCompact(g.date) || `${idx + 1}`,
          dateFormatted: formatDateLong(g.date),
          hasPeriodData,
        };
      }),
    [displayGames, propType, period],
  );

  // All chart data for fixed streak (full history, chronological oldest→newest)
  const allChartData: ChartDataPoint[] = useMemo(
    () =>
      [...(recentGames || [])].reverse().map((g, idx) => {
        const { value, hasPeriodData } = getValueForPeriod(g, propType, period);
        return {
          value,
          date: g.date,
          gameNumber: idx + 1,
          opponent: g.opponent,
          isHome: g.isHome,
          teamScore: g.teamScore,
          opponentScore: g.opponentScore,
          won: g.won,
          label: formatDate(g.date) || `G${idx + 1}`,
          compactLabel: formatDateCompact(g.date) || `${idx + 1}`,
          dateFormatted: formatDateLong(g.date),
          hasPeriodData,
        };
      }),
    [recentGames, propType, period],
  );

  // Count how many displayed games have period data
  const periodDataCount = useMemo(() => {
    if (period === 'full') return displayGames.length;
    return displayGames.filter((g) => getValueForPeriod(g, propType, period).hasPeriodData).length;
  }, [displayGames, propType, period]);

  const handleBarClick = useCallback(
    (index: number) => {
      setExpandedBar(expandedBar === index ? null : index);
    },
    [expandedBar],
  );

  const handleModeChange = useCallback((mode: ViewMode) => {
    setViewMode(mode);
    setExpandedBar(null);
  }, []);

  if (!recentGames?.length) return null;

  // Hit rate — use chartData values (already period-adjusted) so hit rate matches bar heights
  // A push (a margin or total landing on the line) is neither a hit nor a miss
  const { hits, decided, pushes } = hitSummary(
    chartData.map((g) => teamLineOutcome(g, propType, propLine, isUnderBet)),
  );
  const hitRate = decided > 0 ? Math.round((hits / decided) * 100) : 0;
  const directionLabel = getDirectionLabel(propType, isUnderBet);

  // Y-axis range — always include 0 for proper visual scale
  const values = chartData.map((g) => g.value);
  // For spreads, the margin threshold is -propLine (e.g., -16 spread → need margin > +16)
  const refLineValue = propType === 'moneyline' ? 0 : propType === 'spread' ? -propLine : propLine;
  const dataMax = Math.max(...values, refLineValue);
  const dataMin = Math.min(...values, refLineValue, 0);
  const allowNeg = propType === 'moneyline' || propType === 'spread';
  const { ticks: yTicks, minValue, maxValue } = computeNiceTicks(dataMin, dataMax, allowNeg);
  const linePercent = plotFraction(refLineValue, minValue, maxValue) * 100;

  const hasH2hData = (h2hGames?.length ?? 0) > 0;
  const refLineLabel = propType === 'moneyline' ? 'W/L' : formatLineDisplay(refLineValue, propType);

  return (
    <div className="bg-theme-bg/50 rounded-xl p-3 space-y-2">
      {/* Header: animated tabs + hit rate */}
      <div className="flex items-center justify-between gap-2">
        <TeamViewModeTabs
          activeMode={viewMode}
          onModeChange={handleModeChange}
          recentGamesCount={recentGames.length}
          hasH2hData={hasH2hData}
          opponentAbbr={opponentAbbr}
        />
        <motion.span
          key={`${hits}-${decided}-${pushes}`}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className={cn(
            'text-xs font-bold tabular-nums shrink-0 text-right',
            decided === 0
              ? 'text-theme-text-tertiary'
              : hitRate >= 60
                ? 'text-theme-success'
                : hitRate >= 40
                  ? 'text-theme-warning'
                  : 'text-theme-danger',
          )}
        >
          {displayGames.length === 0 ? (
            'No data'
          ) : (
            <>
              {decided > 0
                ? `${hits}/${decided} ${directionLabel} (${hitRate}%)`
                : 'No decided games'}
              {pushes > 0 && (
                // Below sm the count takes its own line, so the header fits a 375px screen
                <span className="block sm:inline">
                  <span className="hidden sm:inline"> · </span>
                  {pushes} push
                </span>
              )}
            </>
          )}
        </motion.span>
      </div>

      {/* Stats summary row */}
      <TeamStatsSummaryRow
        chartData={chartData}
        allChartData={allChartData}
        propLine={propLine}
        propType={propType}
        isUnderBet={isUnderBet}
      />

      {/* Period data info banner */}
      {period !== 'full' && displayGames.length > 0 && periodDataCount < displayGames.length && (
        <div className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-theme-warning/10 text-theme-warning text-[11px]">
          <Info className="w-3 h-3 shrink-0" />
          <span>
            {periodDataCount === 0
              ? `${period.toUpperCase()} data unavailable — showing full-game stats`
              : `${period.toUpperCase()} data available for ${periodDataCount}/${displayGames.length} games`}
          </span>
        </div>
      )}

      {/* Chart area */}
      <AnimatePresence mode="wait">
        {chartData.length > 0 ? (
          <motion.div
            key={`${viewMode}-${propType}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
          >
            {/* Bar chart with Y-axis labels */}
            <div className="relative flex">
              {/* Y-axis labels, each centred on its gridline: same insets as the grid lines below */}
              <div className="relative shrink-0 w-8">
                <div className="absolute inset-x-0 top-4 bottom-7">
                  {yTicks.map((tick) => {
                    const formatted = propType === 'spread' && tick > 0 ? `+${tick}` : String(tick);
                    return (
                      <span
                        key={tick}
                        className="absolute right-1.5 translate-y-1/2 text-[9px] text-theme-text-tertiary/60 tabular-nums text-right leading-none"
                        style={{ bottom: `${plotFraction(tick, minValue, maxValue) * 100}%` }}
                      >
                        {formatted}
                      </span>
                    );
                  })}
                </div>
              </div>

              {/* Chart area */}
              <div className="relative h-48 sm:h-56 flex items-stretch gap-0.5 flex-1 min-w-0 pt-4 pb-7">
                {/* Grid lines + Reference line — share the same coordinate space */}
                <div className="absolute inset-x-0 top-4 bottom-7 pointer-events-none flex flex-col justify-between">
                  {yTicks.map((tick) => (
                    <div key={tick} className="border-t border-theme-border/40" />
                  ))}
                </div>

                {/* Reference line */}
                <div className="absolute inset-x-0 top-4 bottom-7 z-10 pointer-events-none">
                  <motion.div
                    className="absolute left-0 right-0 translate-y-1/2"
                    initial={false}
                    animate={{ bottom: `${linePercent}%` }}
                    transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                  >
                    <div className="flex items-center gap-1">
                      <div
                        className={cn(
                          'flex-1 border-t-2 border-dashed',
                          isCustomLine ? 'border-theme-warning/70' : 'border-theme-primary/70',
                        )}
                      />
                      <span
                        className={cn(
                          'text-[10px] font-bold bg-theme-bg/80 px-1.5 py-0.5 rounded shrink-0',
                          isCustomLine ? 'text-theme-warning' : 'text-theme-primary',
                        )}
                      >
                        {refLineLabel}
                      </span>
                    </div>
                  </motion.div>
                </div>

                {/* Bars */}
                {chartData.map((point, idx) => (
                  <TeamAnimatedBar
                    key={`${viewMode}-${propType}-${point.date || idx}`}
                    point={point}
                    maxValue={maxValue}
                    minValue={minValue}
                    propLine={propLine}
                    propType={propType}
                    isUnderBet={isUnderBet}
                    isExpanded={expandedBar === idx}
                    onClick={() => handleBarClick(idx)}
                    index={idx}
                    barCount={chartData.length}
                  />
                ))}
              </div>
            </div>

            {/* Expanded game detail */}
            <AnimatePresence>
              {expandedBar !== null && chartData[expandedBar] && (
                <TeamGameDetailCard
                  point={chartData[expandedBar]}
                  propType={propType}
                  propLine={propLine}
                  isUnderBet={isUnderBet}
                  teamName={teamName}
                  onClose={() => setExpandedBar(null)}
                />
              )}
            </AnimatePresence>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="h-48 flex items-center justify-center text-xs text-theme-text-tertiary"
          >
            {viewMode === 'H2H'
              ? `No games vs ${opponentAbbr} in recent history`
              : 'No game data available'}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Legend with home/away indicator */}
      <div className="flex items-center justify-center gap-3 text-[10px] text-theme-text-tertiary pt-1">
        <div className="flex items-center gap-1">
          <div className="w-2.5 h-2.5 rounded-sm bg-theme-success" />
          <span>Hit</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-2.5 h-2.5 rounded-sm bg-theme-danger" />
          <span>Miss</span>
        </div>
        {pushes > 0 && (
          <div className="flex items-center gap-1">
            <div className="w-2.5 h-2.5 rounded-sm bg-theme-text-tertiary" />
            <span>Push</span>
          </div>
        )}
        <div className="flex items-center gap-1">
          <div
            className={cn(
              'w-4 h-0 border-t-2 border-dashed',
              isCustomLine ? 'border-theme-warning' : 'border-theme-primary',
            )}
          />
          <span>{isCustomLine ? 'Custom' : 'Line'}</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="w-1.5 h-1.5 rounded-full bg-theme-primary" />
          <span>Home</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="w-1.5 h-1.5 rounded-full bg-theme-text-tertiary" />
          <span>Away</span>
        </div>
      </div>
    </div>
  );
}
